import {
  addConfiguration,
  getConfigurations,
  getConfigurationById,
} from "../../../api/ConfigService";
import { buildCompleteConfig, createMappers } from "../utils/mappingUtils";

/**
 * Service for handling configuration operations
 */
export class ConfigurationService {
  /**
   * Export configuration as JSON file and save to backend
   */
  static async exportConfiguration(
    formValues,
    ageRanges,
    ajvSchemas,
    mappers,
    user,
    filePreview,
    fileMetadata,
    selectedRange,
    selectedSchemaMarket,
    selectedAttribute,
    ingestionMappings,
    prospectMappings,
    enrichConsumerMappings,
    arrayDefaults,
    availableIngestionFields,
    availableIngestionFieldsEnrich
  ) {
    try {
      const completeConfig = buildCompleteConfig(
        formValues,
        ageRanges,
        ajvSchemas,
        mappers,
        user,
        filePreview,
        fileMetadata,
        selectedRange,
        selectedSchemaMarket,
        selectedAttribute,
        ingestionMappings,
        prospectMappings,
        enrichConsumerMappings,
        arrayDefaults,
        availableIngestionFields,
        availableIngestionFieldsEnrich
      );

      const result = await addConfiguration(completeConfig);
      return { success: true, result };
    } catch (error) {
      return { success: false, error: error.message || "An error occurred while exporting" };
    }
  }

  /**
   * Load configurations for a specific region
   */
  static async loadConfigurations(region) {
    try {
      const result = await getConfigurations(region, true);
      return { success: true, data: result || [] };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred while loading configurations",
      };
    }
  }

  /**
   * Load a specific configuration by ID
   */
  static async loadConfiguration(configId) {
    try {
      const result = await getConfigurationById(configId);
      return { success: true, data: result.data };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred while loading configuration",
      };
    }
  }

  /**
   * Preview prospect mapping with given configuration
   */
  static previewMapping(
    filePreview,
    formValues,
    ageRanges,
    ajvSchemas,
    mappers,
    user,
    selectedRange,
    selectedSchemaMarket,
    selectedAttribute,
    ingestionMappings,
    prospectMappings,
    enrichConsumerMappings,
    arrayDefaults,
    availableIngestionFields,
    availableIngestionFieldsEnrich
  ) {
    if (!filePreview || typeof filePreview !== "object") {
      return {
        success: false,
        error: "No source data available. Please upload a CSV file first.",
      };
    }

    try {
      // Build complete config and create mappers
      const completeConfig = buildCompleteConfig(
        formValues,
        ageRanges,
        ajvSchemas,
        mappers,
        user,
        filePreview,
        null,
        selectedRange,
        selectedSchemaMarket,
        selectedAttribute,
        ingestionMappings,
        prospectMappings,
        enrichConsumerMappings,
        arrayDefaults,
        availableIngestionFields,
        availableIngestionFieldsEnrich
      );

      const mapperInstance = createMappers(completeConfig);
      const ingestionMapper = mapperInstance.ingestion;
      const prospectMapper = mapperInstance.createProspect;

      // First apply ingestion mapping
      const ingestionResult = ingestionMapper(filePreview);

      // Check if ingestionResult is valid before proceeding
      if (!ingestionResult || typeof ingestionResult !== "object") {
        throw new Error("Ingestion mapping failed - no valid result returned");
      }

      // Ensure partner field is present, default to partner name from form if missing
      if (!ingestionResult.partner) {
        ingestionResult.partner = formValues.partnerName || "DEFAULT_PARTNER";
      }

      // Then apply prospect mapping
      const prospectResult = prospectMapper(ingestionResult);

      return {
        success: true,
        data: {
          originalData: filePreview,
          intermediateData: ingestionResult,
          mappedData: prospectResult,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: `Error in prospect mapping: ${error.message}`,
        data: {
          originalData: filePreview,
          mappedData: { error: `Error in prospect mapping: ${error.message}` },
          intermediateData: null,
        },
      };
    }
  }
}
